import { Space, Stack, Text, Title } from "@mantine/core";

const Terms: React.FC = () => {
  return (
    <Stack>
      <Title order={1} mt="md">
        Audinate DNA Platform — Terms of Service
      </Title>
      <i>Last Updated: 14 Oct 2025</i>
      <Space h="sm" />
      <Stack gap="md" mb="md">
        <Text fw="bold" size="lg">
          1. Definitions and interpretation
        </Text>
        <Text>
          1.1 Affiliate means any entity that directly or indirectly controls,
          is controlled by or is under common control with a party.
        </Text>
        <Text>
          1.2 Access Credentials means any authentication credential issued by
          Audinate including usernames, passwords, one-time codes, tokens or
          certificates used to access the DNA Platform.
        </Text>
        <Text>
          1.3 Applicable Law means all laws, regulations and mandatory standards
          that apply to the parties under this agreement.
        </Text>
        <Text>
          1.4 Audinate means Audinate Pty Ltd or the relevant contracting
          Audinate entity identified to Licensee.
        </Text>
        <Text>
          1.5 Business Day means a day other than a Saturday, Sunday or public
          holiday in New South Wales, Australia.
        </Text>
        <Text>1.6 Confidential Information has the meaning in clause 7.</Text>
        <Text>
          1.7 DNA Platform means Audinate&apos;s online portal for Licensees to
          access protected documentation, configure and upload firmware and
          manage licence pools for hardware or software.
        </Text>
        <Text>
          1.8 Documentation means the protected documents provided in the DNA
          Platform that relate to licensed products.
        </Text>
        <Text>
          1.9 End User means an individual natural person who is an employee of
          Licensee or a third-party contractor approved by Audinate under clause
          2.4.
        </Text>
        <Text>
          1.10 End User Account means a unique, non-shared account assigned to
          an End User.
        </Text>
        <Text>
          1.11 Firmware means any binary, code, update or related material a
          Licensee uploads or provides for distribution through the DNA
          Platform.
        </Text>
        <Text>
          1.12 Harmful Code means any virus, worm, trojan, backdoor or other
          code designed to disable, disrupt, exfiltrate or provide unauthorised
          access.
        </Text>
        <Text>
          1.13 Licensee means a person or entity licensed by Audinate to build a
          Dante-enabled product, whether hardware or software, that registers
          for and uses the DNA Platform.
        </Text>
        <Text>
          1.14 Licensee Administrator means Licensee personnel designated by the
          Licensee to administer End User Accounts and Access Credentials.
        </Text>
        <Text>
          1.15 Licence Pool means the Licensee&apos;s pool of licences for
          hardware or software as shown in the DNA Platform.
        </Text>
        <Text>
          1.16 Loss means any loss, damage, cost, expense, liability, charge,
          interest or penalty suffered or incurred, including reasonable legal
          and professional fees and costs.
        </Text>
        <Text>
          1.17 Master Agreement means a separate written agreement between
          Audinate and the Licensee that governs the Licensee relationship and
          includes confidentiality terms.
        </Text>
        <Text>
          1.18 Tools means utilities in the DNA Platform used to configure or
          upload firmware.
        </Text>
        <Text>1.19 Terms means these DNA Platform Terms of Service.</Text>
        <Text>
          1.20 Interpretation. Headings are for convenience only and do not
          affect interpretation. “Including” means “including without
          limitation”.
        </Text>
      </Stack>
      <Stack gap="md" mb="md">
        <Text fw="bold" size="lg">
          2. Accounts, eligibility and Licensee Administrators
        </Text>
        <Text>
          2.1 Acceptance. Licensee accepts these Terms by accessing the DNA
          Platform.
        </Text>
        <Text>
          2.2 Eligibility. The DNA Platform is provided for B2B use by Licensees
          only.
        </Text>
        <Text>
          2.3 Licensee Administrators. Licensee must designate one or more
          Licensee Administrators who are authorised to create, grant, modify,
          suspend and revoke End User Accounts and Access Credentials.
        </Text>
        <Text>
          2.4 Third-party contractors. End User Accounts for third-party
          contractors require Audinate&apos;s prior written approval. Licensee
          must ensure each approved contractor is under Licensee&apos;s direct
          control and bound to confidentiality and security obligations no less
          protective than these Terms.
        </Text>
        <Text>
          2.5 Affiliate access. Licensee may allow its Affiliates to use the DNA
          Platform under Licensee&apos;s account if each Affiliate complies with
          these Terms and Licensee remains responsible for all Affiliate
          activity.
        </Text>
        <Text>
          2.6 Relationship to Master Agreement. The following apply to the DNA
          Platform and prevail over any inconsistent term in these Terms:
        </Text>
        <Text component="div">
          <Stack gap="md" ml="lg">
            <Text>
              a) IP ownership and feedback under the Master Agreement apply to
              Documentation, Tools and any material surfaced in the DNA Platform
            </Text>
            <Text>
              b) IP infringement indemnity under the Master Agreement applies to
              Documentation and Tools; the indemnity in clause 12.1 is
              additional and not limited by the Master Agreement;
            </Text>
            <Text>
              c) Export controls, sanctions and anti-bribery terms apply;
            </Text>
            <Text>
              d) Publicity and trademark use terms apply to any reference to the
              Licensee in or about the DNA Platfor
            </Text>
            <Text>e) Subcontracting terms apply to the DNA Platform;</Text>
            <Text>
              f) Audit rights under the Master apply in addition to clause 15;
              and
            </Text>
            <Text>
              g) Liability caps and carve-outs under the Master Agreement
              replace clause 13.1 to the extent of conflict.
            </Text>
          </Stack>
        </Text>
      </Stack>
      <Stack gap="md" mb="md">
        <Text fw="bold" size="lg">
          3. Access rights, licence and restrictions
        </Text>
        <Text>
          3.1 Access grant. Subject to these Terms, Audinate grants Licensee a
          non-exclusive, non‑transferable and revocable right for
          Licensee&apos;s End Users to access the DNA Platform for
          Licensee&apos;s internal purposes.
        </Text>
        <Text>
          3.2 Restrictions. Licensee must not and must ensure End Users do not:
        </Text>
        <Stack gap="md" ml="lg">
          <Text>a) share End User Accounts or credentials</Text>
          <Text>
            b) reverse engineer, decompile or attempt to derive source code
            except to the extent permitted by law that cannot be excluded
          </Text>
          <Text>
            c) circumvent technical controls or exceed use limits shown in the
            DNA Platform
          </Text>
          <Text>d) access the DNA Platform to build a competing service.</Text>
        </Stack>
      </Stack>
      <Stack gap="md" mb="md">
        <Text fw="bold" size="lg">
          4. Licensee Account Administration
        </Text>
        <Text>
          4.1 Unique users. Each End User Account must be assigned to one
          natural person and must not be shared.
        </Text>
        <Text>
          4.2 Identity hygiene. Licensee must enforce strong credentials and any
          multi-factor authentication made available in the DNA Platform.
        </Text>
        <Text>
          4.3 Contractor approvals. Licensee must obtain Audinate&apos;s prior
          written approval before provisioning an End User Account for a
          third-party contractor.
        </Text>
        <Text>
          4.4 Affiliate boundaries. Licensee must not allow resellers or
          distributors to access the DNA Platform unless separately authorised
          by Audinate in writing.
        </Text>
        <Text>
          4.5 Records. Licensee must maintain records sufficient to demonstrate
          compliance with this clause 4 for at least 12 months and must provide
          reasonable cooperation for checks under clause 14.
        </Text>
        <Text>
          4.6 Responsibility for End Users. Licensee is responsible for all acts
          and omissions of its End Users, Affiliates and approved contractors
          and for all access made using its End User Accounts and Access
          Credentials, whether or not authorised by Licensee.
        </Text>
        <Text>
          4.7 Authorisation. The person accepting these Terms represents that
          they are authorised to bind the Licensee. If they are not authorised,
          acceptance binds that person in their individual capacity and they
          must not permit others to access the DNA Platform under that account.
        </Text>
      </Stack>
      <Stack gap="md" mb="md">
        <Text fw="bold" size="lg">
          5. Email or role changes, off-boarding, on-demand access review
        </Text>
        <Text>
          5.1 Email or role changes. If an End User changes email or other
          unique identifier to a domain not controlled by Licensee or changes
          role such that access is no longer required, Licensee must disable or
          remove the End User&apos;s access and revoke related Access
          Credentials as soon as practicable and in any event within 3 Business
          Days.
        </Text>
        <Text>
          5.2 Leavers. When an End User&apos;s employment or engagement ends,
          Licensee must complete off-boarding within 3 Business Days, including
          disabling the End User Account and revoking related Access
          Credentials.
        </Text>
        <Text>
          5.3 On-demand access review. On Audinate&apos;s written notice,
          Licensee must conduct and certify an access review within 30 days,
          remove access for any End User who no longer requires it and provide
          reasonable evidence of the review.
        </Text>
        <Text>
          5.4 Evidence retention. Licensee must retain evidence of off-boarding
          actions and any access review for at least 12 months.
        </Text>
        <Text>
          5.5 No mandatory credential rotation. These Terms do not impose
          periodic rotation of Access Credentials. Licensee must rotate Access
          Credentials when roles change or when compromise is suspected.
        </Text>
      </Stack>
      <Stack gap="md" mb="md">
        <Text fw="bold" size="lg">
          6. Security and incident notice
        </Text>
        <Text>
          6.1 Security. Licensee is responsible for the security of End User
          Accounts, Access Credentials and any device used to access the DNA
          Platform.
        </Text>
        <Text>
          6.2 Notice. Licensee must promptly notify Audinate of any actual or
          suspected unauthorised access to the DNA Platform, any End User
          Account or any Access Credential and must assist Audinate to mitigate
          harm.
        </Text>
      </Stack>
      <Stack gap="md" mb="md">
        <Text fw="bold" size="lg">
          7. Confidential Information
        </Text>
        <Text>
          7.1 Scope. Documentation, Tools, firmware utilities, Licence Pools and
          all non-public information exposed in the DNA Platform are Audinate
          Confidential Information.
        </Text>
        <Text>
          7.2 Obligations. Licensee must keep Confidential Information secret,
          use it only for the purposes of the DNA Platform and protect it with
          at least the same care it uses for its own confidential information of
          similar importance.
        </Text>
        <Text>
          7.3 Permitted disclosure. Licensee may disclose Confidential
          Information to its personnel and approved contractors who need to know
          it and are bound by confidentiality terms no less protective than
          these Terms.
        </Text>
        <Text>
          7.4 Exclusions. Information that is public without breach, already
          known on a non-confidential basis, independently developed or lawfully
          received from a third party without restriction is not Confidential
          Information.
        </Text>
        <Text>
          7.5 Master Agreement confidentiality. The confidentiality terms of the
          Master Agreement are incorporated by reference and apply to
          Confidential Information disclosed through the DNA Platform. If there
          is any inconsistency between the Master Agreement confidentiality
          terms and this clause 7, the stricter obligation or longer survival
          applies. Nothing in the Master Agreement limits the Licensee&apos;s
          obligations under clauses 4, 5 and 6.
        </Text>
      </Stack>
      <Stack gap="md" mb="md">
        <Text fw="bold" size="lg">
          8. Platform changes, maintenance and support
        </Text>
        <Text>
          8.1 Changes. Audinate may modify the DNA Platform, Documentation or
          Tools from time to time.
        </Text>
        <Text>
          8.2 Maintenance. The DNA Platform may be unavailable during planned or
          emergency maintenance.
        </Text>
        <Text>
          8.3 Support. Audinate will provide support through its standard
          support channels.
        </Text>
      </Stack>
      <Stack gap="md" mb="md">
        <Text fw="bold" size="lg">
          9. Suspension and termination
        </Text>
        <Text>
          9.1 Mis-association and inactivity. Audinate may suspend or restrict
          any End User Account or Access Credential where Audinate reasonably
          suspects mis-association, inaccuracy or non-compliance with clause 4
          or 5. Audinate may also suspend End User Accounts that have been
          inactive for 90 consecutive days. Audinate will send a warning at
          least 7 days before suspension for inactivity.
        </Text>
        <Text>
          9.2 Security risk. Audinate may suspend access immediately to address
          a security, legal or operational risk.
        </Text>
        <Text>
          9.3 Termination for cause. Either party may terminate these Terms for
          material breach not cured within 30 days of notice.
        </Text>
        <Text>
          9.4 Effect of termination. On termination Licensee must cease access
          and destroy or return Audinate Confidential Information on request,
          except as required to be retained by law.
        </Text>
      </Stack>
      <Stack gap="md" mb="md">
        <Text fw="bold" size="lg">
          10. Warranties and disclaimers
        </Text>
        <Text>
          10.1 Mutual warranties. Each party warrants it has authority to enter
          into these Terms.
        </Text>
        <Text>
          10.2 Service disclaimer. The DNA Platform, Documentation and Tools are
          provided “as is” except for any statutory guarantee that cannot be
          excluded by law. Audinate does not warrant that the DNA Platform will
          be uninterrupted or error free.
        </Text>
      </Stack>
      <Stack gap="md" mb="md">
        <Text fw="bold" size="lg">
          11. Firmware uploads and distribution
        </Text>
        <Text>
          11.1 Licence to host and distribute. Licensee grants Audinate a
          non-exclusive, worldwide, royalty-free licence to host, copy, cache,
          scan and distribute Firmware through the DNA Platform and related
          services and to use Licensee&apos;s marks as reasonably necessary to
          identify the Firmware.
        </Text>
        <Text>
          11.2 Attestations. By uploading Firmware the uploader represents that
          they are an employee or authorised contractor of the Licensee and are
          authorised to upload on the Licensee&apos;s behalf.
        </Text>
        <Text>11.3 Warranties. Licensee warrants that the Firmware:</Text>
        <Stack gap="md" ml="lg">
          <Text>
            a) is free of Harmful Code and has been validated by Licensee for
            field use;
          </Text>
          <Text>
            b) does not infringe any third-party rights and complies with all
            open-source obligations applicable to it;
          </Text>
          <Text>
            c) complies with export, sanctions and other Applicable Law; and
          </Text>
          <Text>
            d) is accompanied by accurate release notes and versioning.
          </Text>
        </Stack>
        <Text>
          11.4 Supply chain and provenance. On written request, Audinate may
          require Licensee within 30 days to provide reasonable evidence of
          build provenance for a specific Firmware release, including the
          signing certificate chain, release artefact hash and a bill of
          materials or equivalent record sufficient to identify third-party
          components.
        </Text>
        <Text>
          11.5 Control measures. Audinate may at its discretion refuse,
          quarantine, remove or roll back distribution of any Firmware and/or
          suspend distribution where Audinate reasonably suspects non-compliance
          with this clause 11 or a security, goodwill, legal or operational
          risk.
        </Text>
        <Text>
          11.6 No validation duty. Audinate is not obliged to review, test or
          validate Firmware and distribution by Audinate does not constitute
          approval or acceptance of the Firmware.
        </Text>
        <Text>
          11.7 Indemnity. In addition to any indemnity under the Master
          Agreement, Licensee indemnifies Audinate against Loss arising from or
          in connection with Firmware, including third-party claims, regulatory
          action, device failures or security incidents to the extent caused by
          Licensee&apos;s breach of this clause 11 or the Firmware itself.
        </Text>
      </Stack>
      <Stack gap="md" mb="md">
        <Text fw="bold" size="lg">
          12. Indemnities
        </Text>
        <Text>
          12.1 Licensee indemnity. To the extent permitted by law, Licensee
          indemnifies Audinate against Loss arising from unauthorised access,
          misuse or disclosure of Audinate Confidential Information or the DNA
          Platform through any End User Account or Access Credential where
          Licensee failed to comply with clauses 4, 5 or 6.
        </Text>
        <Text>
          12.2 Conditions. Audinate will provide prompt notice, reasonable
          cooperation and sole control of the defence to Licensee. The indemnity
          is subject to the liability cap in clause 13.
        </Text>
      </Stack>
      <Stack gap="md" mb="md">
        <Text fw="bold" size="lg">
          13. Liability
        </Text>
        <Text>
          13.1 Cap. Subject to clause 13.3, Audinate&apos;s aggregate liability
          arising out of or in connection with these Terms is limited to the
          greater of the fees paid or payable by Licensee for the DNA Platform
          in the 12 months before the event giving rise to liability and
          A$10,000.
        </Text>
        <Text>
          13.2 Exclusions. Neither party is liable for indirect, special or
          consequential loss, loss of revenue, loss of profits or loss of data.
        </Text>
        <Text>
          13.3 Non-excludable liability. Nothing in these Terms limits liability
          that cannot be limited or excluded under Applicable Law or liability
          for fraud or wilful misconduct.
        </Text>
        <Text>
          13.4 Indemnities. Clause 13.1 does not limit any indemnity that
          applies under clause 12.1 or 11.7.
        </Text>
      </Stack>
      <Stack gap="md" mb="md">
        <Text fw="bold" size="lg">
          14. Compliance and export controls
        </Text>
        <Text>
          Licensee must comply with all Applicable Law relating to export,
          sanctions, anti-bribery and anti-corruption. Licensee must not allow
          access from an embargoed jurisdiction or to a prohibited party.
        </Text>
      </Stack>
      <Stack gap="md" mb="md">
        <Text fw="bold" size="lg">
          15. Audit and verification
        </Text>
        <Text>
          15.1 On-demand audit. On at least 30 days written notice Audinate may
          require Licensee to conduct and certify an access review. Audinate may
          request the evidence described in clause 11.4 for a specific Firmware
          release under clause 5.3 and to provide reasonable evidence of
          compliance with clauses 4, 5, 7 and 11.
        </Text>
        <Text>
          15.2 Desk audit. Where reasonably required Audinate may conduct a
          remote desk audit of the records described in clause 4.5 during
          business hours on reasonable notice. Audinate will use information
          obtained only to verify compliance.
        </Text>
      </Stack>
      <Stack gap="md" mb="md">
        <Text fw="bold" size="lg">
          16. Privacy and data handling
        </Text>
        <Text>
          Audinate will handle personal information in accordance with its
          Privacy Notice as updated from time to time. Licensee is responsible
          for providing all required notices to its End Users.
        </Text>
      </Stack>
      <Stack gap="md" mb="md">
        <Text fw="bold" size="lg">
          17. General
        </Text>
        <Text>
          17.1 Assignment. Licensee must not assign these Terms without
          Audinate&apos;s prior written consent.
        </Text>
        <Text>
          17.2 Subcontracting. Audinate may use subcontractors and remains
          responsible for their acts.
        </Text>
        <Text>
          17.3 Force majeure. Neither party is liable for failure caused by an
          event beyond its reasonable control.
        </Text>
        <Text>
          17.4 Notices. Notices must be in writing and sent to the contacts
          nominated by the parties in the DNA Platform or otherwise notified.
        </Text>
        <Text>
          17.5 Severability. If any part of these Terms is unenforceable the
          remainder remains in effect.
        </Text>
        <Text>17.6 Waiver. A failure to enforce is not a waiver.</Text>
        <Text>
          17.7 Entire agreement. These Terms are the entire agreement for the
          DNA Platform and replace any prior terms for the same subject matter.
        </Text>
        <Text>
          17.8 Order of precedence. If Licensee signed a separate written
          agreement for the DNA Platform with Audinate and it expressly
          overrides these Terms, that agreement prevails to the extent of
          conflict.
        </Text>
      </Stack>
      <Stack gap="md" mb="md">
        <Text fw="bold" size="lg">
          18. Governing law and dispute resolution
        </Text>
        <Text>
          18.1 Governing law. These Terms are governed by the laws of New South
          Wales, Australia.
        </Text>
        <Text>
          18.2 Disputes. The parties will attempt to resolve disputes by good
          faith discussion between senior representatives. Courts of New South
          Wales have exclusive jurisdiction.
        </Text>
      </Stack>
    </Stack>
  );
};

export default Terms;
